import { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Paper, IconButton, Typography, CircularProgress, Stack, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { useLanguage } from '../../../utils/i18n';
import { apiVss } from '../../../api/axiosClient';
import { API_BASES } from '../../../config/constants';

export default function VideoStreaming({ open, onClose, vehicle }) {
  const { t } = useLanguage();
  const [token, setToken] = useState('');
  const [loadingToken, setLoadingToken] = useState(false); // Loading API Token
  const [loadingIframe, setLoadingIframe] = useState(false); // Loading Iframe Video
  const [error, setError] = useState(null);

  // State untuk memilih kamera (Default: Camera 1)
  const [selectedCameras, setSelectedCameras] = useState(['1']);
  const [installedCameras, setInstalledCameras] = useState([]);

  useEffect(() => {
    if (!token) return;
    try {
      const cached = localStorage.getItem('vehicle_installed_camera');
      if (cached) {
        setInstalledCameras(JSON.parse(cached));
        return;
      }
    } catch (e) {
      console.error('Error parsing cached cameras', e);
    }

    const imei = String(
      vehicle?.integration_imei || (vehicle?.raw && vehicle.raw.integration_imei) || (vehicle?.info && vehicle.info.integration_imei) || ''
    ).trim();

    const params = new URLSearchParams();
    params.append('pageNum', '-1');
    params.append('pageCount', '-1');
    params.append('token', String(token));
    params.append('scheme', 'https');
    params.append('lang', 'en_US');
    if (imei) params.append('keyword', imei);

    apiVss
      .post('/vehicle/findAll.action', params, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      })
      .then((resp) => {
        const responseData = resp.data?.data || resp.data;
        const dataList = responseData?.dataList || responseData;
        if (dataList && Array.isArray(dataList)) {
          const simplifiedData = dataList.map((item) => ({
            deviceno: item.deviceno || item.imei,
            channelname: item.channelname
          }));
          const existing = JSON.parse(localStorage.getItem('vehicle_installed_camera') || '[]');
          const merged = [...existing.filter((e) => !simplifiedData.some((s) => s.deviceno === e.deviceno)), ...simplifiedData];
          localStorage.setItem('vehicle_installed_camera', JSON.stringify(merged));
          setInstalledCameras(merged);
        }
      })
      .catch((err) => console.error('Failed to fetch installed cameras', err));
  }, [token, vehicle]);

  // Mengambil Token Howen saat Dialog Terbuka
  useEffect(() => {
    if (!open) return;

    const fetchToken = async () => {
      setLoadingToken(true);
      setError(null);
      try {
        const resp = await fetch(`${API_BASES.HOWEN_API}/get-token`, {
          method: 'GET',
          headers: { Accept: 'application/json' }
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();

        if (data && data.data && data.data.token) {
          setToken(String(data.data.token));
        } else {
          throw new Error('Token tidak ditemukan di response');
        }
      } catch (err) {
        console.error('Fetch token error:', err);
        setError(t('mapsTracking.failedToGetCameraToken', 'Gagal mengambil akses token kamera.'));
      } finally {
        setLoadingToken(false);
      }
    };

    fetchToken();
  }, [open, t]);

  // Menggabungkan array channel kamera (misal: ['1', '2'] menjadi "1_2")
  const buildChsFromSelected = useCallback((selectedArr) => {
    if (!selectedArr || selectedArr.length === 0) return '';
    return selectedArr.join('_');
  }, []);

  // Merakit URL iframe
  const iframeSrc = useMemo(() => {
    if (!token || !vehicle?.raw?.raw?.integration_imei) return '';

    const params = new URLSearchParams();
    params.set('token', token);
    params.set('deviceId', vehicle?.raw?.raw?.integration_imei); // Mengambil IMEI
    params.set('chs', buildChsFromSelected(selectedCameras));
    params.set('stream', '0');
    params.set('wnum', String(Math.max(1, selectedCameras.length)));
    params.set('panel', '1');
    params.set('buffer', '2000');

    const baseUrl = `${API_BASES.VSS}/apiPage/RealVideo.html`;
    return `${baseUrl}?${params.toString()}`;
  }, [token, vehicle, selectedCameras, buildChsFromSelected]);

  // Nyalakan ulang loading setiap kali URL Iframe berubah
  useEffect(() => {
    if (iframeSrc) {
      setLoadingIframe(true);
    }
  }, [iframeSrc]);

  const handleToggleCamera = (camNum) => {
    setSelectedCameras((prev) => {
      if (prev.includes(camNum)) {
        // Jangan biarkan kosong, minimal 1 kamera aktif
        if (prev.length === 1) return prev;
        return prev.filter((c) => c !== camNum);
      }
      return [...prev, camNum].sort();
    });
  };

  // Jika tidak sedang dibuka, jangan render apa pun
  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 9999, // Z-index tertinggi agar menutupi Map dan Popover
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)',
        bgcolor: 'rgba(0,0,0,0.6)',
        animation: 'fadeIn 0.2s ease-out',
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 }
        }
      }}
    >
      <Paper
        elevation={24}
        sx={{
          width: '85%', // Lebar Dialog
          height: '85%', // Tinggi Dialog
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#fff',
          boxShadow: '0 16px 40px rgba(0,0,0,0.5)'
        }}
      >
        {/* HEADER DIALOG */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid #e2e8f0',
            bgcolor: '#f8fafc'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <VideocamOutlinedIcon sx={{ color: '#485D9A' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                Live Camera - {vehicle?.fleet_name || t('mapsTracking.vehicle', 'Kendaraan')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                IMEI: {vehicle?.imei || t('mapsTracking.noImei', 'Tidak ada IMEI')}
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {/* Opsi Toggle Channel Kamera */}
            {(() => {
              const imei = String(vehicle?.raw?.raw?.integration_imei || vehicle?.integration_imei || '').trim();
              let cams = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
              const installed = installedCameras.find((c) => String(c.deviceno) === imei);
              if (installed && installed.channelname) {
                const channels = installed.channelname.split(';').filter((ch) => ch.trim() !== '');
                if (channels.length > 0) {
                  cams = channels.map((ch) => ch.replace(/\D/g, ''));
                }
              }
              return cams.map((cam) => (
                <Button
                  key={cam}
                  size="small"
                  variant={selectedCameras.includes(cam) ? 'contained' : 'outlined'}
                  onClick={() => handleToggleCamera(cam)}
                  sx={{ minWidth: 40, p: 0.5, borderRadius: 1 }}
                >
                  CH{cam}
                </Button>
              ));
            })()}

            <Box sx={{ width: 1, height: 24, borderRight: '1px solid #ccc', mx: 1 }} />

            <IconButton onClick={onClose} size="small" color="error" sx={{ bgcolor: '#ffebee' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        {/* KONTEN IFRAME */}
        <Box sx={{ flexGrow: 1, position: 'relative', bgcolor: '#e2e8f0' }}>
          {loadingToken && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                zIndex: 10
              }}
            >
              <CircularProgress color="primary" />
              <Typography color="text.secondary" variant="body2" fontWeight="bold">
                {t('mapsTracking.fetchingToken', 'Mengambil Token...')}
              </Typography>
            </Box>
          )}

          {error && (
            <Box
              sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', zIndex: 10 }}
            >
              <Typography color="error" fontWeight="bold">
                {error}
              </Typography>
              <Button variant="outlined" color="primary" sx={{ mt: 2 }} onClick={() => setToken('')}>
                {t('mapsTracking.tryAgain', 'Coba Lagi')}
              </Button>
            </Box>
          )}

          {loadingIframe && !loadingToken && !error && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                zIndex: 10
              }}
            >
              <CircularProgress size={30} sx={{ color: '#485D9A' }} />
              <Typography sx={{ color: '#485D9A', fontWeight: 'bold', fontSize: '0.85rem' }}>
                {t('mapsTracking.loadingCamera', 'Memuat Camera...')}
              </Typography>
            </Box>
          )}

          {!loadingToken && !error && iframeSrc && (
            <iframe
              key={iframeSrc}
              src={iframeSrc}
              title="Live Streaming Vehicle"
              width="100%"
              height="100%"
              onLoad={() => setLoadingIframe(false)} // Matikan loading saat iframe selesai render
              style={{ border: 'none', visibility: loadingIframe ? 'hidden' : 'visible' }}
              allowFullScreen
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
}
